import { EventV2 } from '@front-app/services/EventsApi/EventModel';
import {
	addMonths,
	addSeconds,
	differenceInDays,
	differenceInCalendarMonths,
	isAfter,
	startOfToday,
	isEqual,
	isBefore,
	endOfToday,
} from 'date-fns';

// Helpers
const isAfterOrEqual = (d1: Date, d2: Date): boolean => isAfter(d1, d2) || isEqual(d1, d2);
const isBeforeOrEqual = (d1: Date, d2: Date): boolean => isBefore(d1, d2) || isEqual(d1, d2);

// Active events started on or before the end of today AND end on or after the start of today
export const isActiveEvent = (e: EventV2): boolean => isBeforeOrEqual(e.startDate, endOfToday()) && isAfterOrEqual(e.endDate, startOfToday());
export const isLaterTodayOrFutureEvent = (e: EventV2): boolean => isAfter(e.startDate, Date.now());
export const isFutureEvent = (e: EventV2): boolean => !isActiveEvent(e) && isAfter(e.startDate, endOfToday());
export const isPastEvent = (e: EventV2): boolean => !isActiveEvent(e) && !isFutureEvent(e);
export const isNotPastEvent = (e: EventV2): boolean => !isPastEvent(e);
export const isComingSoonEvent = (e: EventV2): boolean => {
	if (!isFutureEvent(e)) {
		return false;
	}

	// Check if the event starts in the current month
	if (differenceInCalendarMonths(e.startDate, new Date()) === 0) {
		return true;
	}

	// Calculate if the current month has less than 7 days left
	let endOfCurrentMonth = addMonths(new Date(), 1);
	endOfCurrentMonth.setMilliseconds(0);
	endOfCurrentMonth.setSeconds(0);
	endOfCurrentMonth.setMinutes(0);
	endOfCurrentMonth.setHours(0);
	endOfCurrentMonth.setDate(1);
	endOfCurrentMonth = addSeconds(endOfCurrentMonth, -1);

	const remainingDays = differenceInDays(endOfCurrentMonth, new Date());

	// Check if the event is from the next month if the current month has less than 7 days left
	if (remainingDays <= 7) {
		const endOfNextMonth = addMonths(endOfCurrentMonth, 1);
		return differenceInCalendarMonths(endOfNextMonth, e.startDate) === 0;
	}

	return false;
};

// Sorting
export const orderEventsOldToNew = (a: EventV2, b: EventV2): number => {
	const startDiff = a.startDate.getTime() - b.startDate.getTime();
	// If start date is the same, subsort by end date
	if (startDiff === 0) {
		return a.endDate.getTime() - b.endDate.getTime();
	}
	return startDiff;
};
